// Contas do app do motorista (puras; testadas em test/app/motorista.test.js):
// os dados da carga que ele completa, o que ele ganha em cada carga, o saldo
// até hoje, o motivo da despesa sem nota e o jeito de entrar no iPhone.
import { agenciamentosPendentes, creditosDaViagem, montarFechamento, previaDoAcerto } from './acerto.js';

const centavos = (v) => Math.round((Number(v) || 0) * 100);
const vazio = (v) => v === null || v === undefined || String(v).trim() === '';

// ---------------------------------------------------------------------
// Dados da carga que o motorista completa (o banco confere a mesma lista)
// ---------------------------------------------------------------------
export const CAMPOS_DA_CARGA = ['cte_numero', 'mercadoria', 'peso_kg', 'data_descarga', 'origem_cidade', 'origem_uf', 'destino_cidade', 'destino_uf'];

export const NOMES_DOS_CAMPOS = {
  cte_numero: 'CT-e / Manifesto',
  mercadoria: 'tipo de carga',
  peso_kg: 'peso',
  data_descarga: 'descarga',
  origem_cidade: 'origem',
  origem_uf: 'UF da origem',
  destino_cidade: 'destino',
  destino_uf: 'UF do destino',
};

// O motorista mexe no campo se está vazio ou se foi ele que preencheu (o que
// o escritório lançou fica com o escritório).
export function motoristaMexe(frete, campo) {
  return vazio(frete[campo]) || (frete.campos_do_motorista ?? []).includes(campo);
}

export function faltaDadoNaCarga(frete) {
  return CAMPOS_DA_CARGA.some((c) => vazio(frete[c]));
}

// "CT-e / Manifesto e peso" (para o escritório ver o que veio do motorista).
export function textoDosCamposDoMotorista(frete) {
  const nomes = (frete.campos_do_motorista ?? [])
    .filter((c) => c !== 'origem_uf' && c !== 'destino_uf' && c !== 'entregue_em')
    .map((c) => NOMES_DOS_CAMPOS[c] ?? c);
  if (nomes.length === 0) return null;
  return nomes.length === 1 ? nomes[0] : `${nomes.slice(0, -1).join(', ')} e ${nomes.at(-1)}`;
}

// ---------------------------------------------------------------------
// O que ele ganha em cada carga
// ---------------------------------------------------------------------
const ROTULOS = {
  comissao: 'Comissão',
  valor_por_carga: 'Valor por carga',
  repasse_estadia: 'Repasse da estadia',
  agenciamento: 'Agenciamento',
  valor_por_viagem: 'Valor por viagem',
  diaria: 'Diárias',
  fixo_mensal: 'Fixo',
  bonus: 'Bônus',
  outro: 'Outro crédito',
};

// Os créditos que já entraram num acerto (carga acertada) ou os previstos
// pelas regras cadastradas. A carga dos programas antigos foi acertada neles
// (sem o detalhe aqui).
//   { situacao: 'prevista' | 'acertada' | 'importada', itens: [{ subtipo, valor, descricao }] }
export function ganhosDaCarga(frete, { viagem, regras = [], lancamentos = [], colaboradorId }) {
  if (frete.importacao_id) return { situacao: 'importada', itens: [] };
  const creditos = lancamentos.filter((l) => !l.deleted_at && l.tipo === 'credito');
  const lancados = creditos.filter((l) => l.frete_id === frete.id);
  if (lancados.length > 0 || frete.acerto_id) {
    return { situacao: 'acertada', itens: lancados.map((l) => ({ subtipo: l.subtipo, valor: Number(l.valor), descricao: l.descricao ?? null })) };
  }
  const itens = [
    ...creditosDaViagem(viagem, [frete], regras, { jaLancados: creditos }).filter((c) => c.frete_id === frete.id),
    ...agenciamentosPendentes([frete], colaboradorId, creditos),
  ];
  return { situacao: 'prevista', itens: itens.map((c) => ({ subtipo: c.subtipo, valor: Number(c.valor), descricao: c.descricao })) };
}

// { titulo: 'Comissão', detalhe: '13% sobre R$ 7.800,00 (frete bruto)' }
export function textoDoGanho(item) {
  const primeira = String(item.descricao ?? '').split(' · ')[0];
  let detalhe = null;
  if (item.subtipo === 'comissao') detalhe = primeira.replace(/^Comissão de /, '') || null;
  if (item.subtipo === 'repasse_estadia') detalhe = /\((.*)\)$/.exec(primeira)?.[1] ?? null;
  return { titulo: ROTULOS[item.subtipo] ?? 'Ganho', detalhe };
}

// ---------------------------------------------------------------------
// Saldo até hoje
// ---------------------------------------------------------------------
const maisRecente = (a, b) => String(b.fechado_em ?? '').localeCompare(String(a.fechado_em ?? ''));

// A prévia do próximo acerto (como o escritório veria hoje) e os acertos
// fechados que ainda não foram pagos. aReceber > 0: a empresa paga a ele;
// < 0: ele recebeu a mais (em vales) do que ganhou.
export function saldoDoMotorista({ colaboradorId, ate, viagens = [], fretes = [], lancamentos = [], regras = [], acertos = [] }) {
  const dele = acertos
    .filter((a) => !a.deleted_at && a.colaborador_id === colaboradorId && a.status !== 'rascunho')
    .sort(maisRecente);
  const previa = previaDoAcerto({ colaboradorId, ate, viagens, fretes, lancamentos, regras, ultimoAcerto: dele[0] ?? null });
  const { totais } = montarFechamento(previa);
  const marcados = (lista) => lista.filter((x) => x.incluir);
  const naoPagos = dele.filter((a) => a.status === 'fechado');
  const deAcertos = naoPagos.reduce((s, a) => s - centavos(a.saldo), 0);
  return {
    ate,
    totais,
    ganhos: [
      ...marcados(previa.viagens).flatMap((v) => v.creditos),
      ...marcados(previa.agenciamentos).map((a) => a.credito),
      ...marcados(previa.fixos).map((f) => f.credito),
      ...marcados(previa.creditos).map((x) => ({ ...x.lancamento, valor: Number(x.lancamento.valor) })),
    ],
    vales: marcados(previa.vales).map((x) => x.lancamento),
    despesas: marcados(previa.despesas).map((x) => x.lancamento),
    descontos: marcados(previa.descontos).map((x) => x.lancamento),
    acertosNaoPagos: naoPagos,
    ultimoAcerto: dele[0] ?? null,
    // Agenciamentos que ficam a receber quando o cliente pagar o adiantamento.
    esperando: previa.agenciamentos.filter((a) => !a.incluir).map((a) => ({ ...a.credito, frete: a.frete })),
    // Da prévia (o saldo do acerto é "o que ele deve": o sinal inverte) e dos
    // acertos fechados sem pagamento.
    aReceber: (deAcertos - centavos(totais.saldo)) / 100,
  };
}

// ---------------------------------------------------------------------
// Despesa sem nota
// ---------------------------------------------------------------------
export const MOTIVOS_SEM_NOTA = [
  ['Chapa', 'Chapa (carga ou descarga)'],
  ['Lavação', 'Lavação'],
  ['Borracharia', 'Borracharia'],
  ['Estacionamento', 'Estacionamento'],
  ['Ajudante', 'Ajudante'],
  ['outro', 'Outro motivo'],
];

// O motivo gravado: o da lista ou o que ele escreveu em "Outro motivo".
export function motivoSemNota(escolha, outro) {
  if (!escolha) return null;
  if (escolha !== 'outro') return escolha;
  const texto = String(outro ?? '').trim().replace(/\s+/g, ' ');
  return texto.length >= 2 ? texto.slice(0, 120) : null;
}

// ---------------------------------------------------------------------
// Entrar pelo link
// ---------------------------------------------------------------------
// iPhone ou iPad (o iPad novo se apresenta como Mac, mas tem toque).
export function ehIphone(nav = globalThis.navigator) {
  if (!nav) return false;
  return /iPhone|iPad|iPod/.test(nav.userAgent ?? '') || (nav.platform === 'MacIntel' && nav.maxTouchPoints > 1);
}

// Aberto pelo ícone do app instalado, e não no navegador.
export function abertoPeloIcone(win = globalThis.window) {
  return Boolean(win?.navigator?.standalone) || Boolean(win?.matchMedia?.('(display-mode: standalone)')?.matches);
}

// O código do link do app (do motorista ou do agenciador), de um link
// colado (ou do código sozinho).
export function codigoDoLink(texto) {
  const t = String(texto ?? '').trim();
  const doLink = /#\/(?:motorista|agenciador)\/([A-Za-z0-9_-]{16,})/.exec(t);
  if (doLink) return doLink[1];
  return /^[A-Za-z0-9_-]{16,}$/.test(t) ? t : null;
}

// A tela de entrada do link colado: a do agenciador ou a do motorista (o
// código sozinho vai para a do motorista; o servidor sabe quem é).
export function rotaDoLink(texto) {
  const codigo = codigoDoLink(texto);
  if (!codigo) return null;
  return /#\/agenciador\//.test(String(texto)) ? `/agenciador/${codigo}` : `/motorista/${codigo}`;
}
